import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { api } from "@/lib/api";
import {
  ArchetypeSection,
  FrameworkSection,
  DimensionsSection,
  YourThreeSection,
  InterpretationSection,
  TalkItThroughSection,
} from "@/components/results/ResultSections";
import { LeadCapture } from "@/components/results/LeadCapture";
import { CounterpartInvite } from "@/components/results/CounterpartInvite";
import { GoDeeper } from "@/components/results/GoDeeper";
import { ResultsPrint } from "@/components/results/ResultsPrint";
import { SphereMark } from "@/components/brand/SphereMark";
import { Printer } from "lucide-react";

export default function Results() {
  const { id, token } = useParams();
  const savedMode = !!token;
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const p = savedMode ? api.getResultsByToken(token) : api.getResults(id);
    p.then(setData).catch(() => setError(true));
  }, [id, token, savedMode]);

  // Private results pages must not be indexed.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-white text-center">
        <p className="text-neutral-600">We couldn&apos;t find that diagnostic.</p>
        <button
          onClick={() => navigate("/")}
          className="tw-btn-primary rounded-sm px-6 py-3 text-sm font-semibold uppercase tracking-wide"
        >
          Start over
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <SphereMark size={20} className="tw-float" />
      </div>
    );
  }

  const r = data.results;
  const about = data.about || {};

  return (
    <>
      <div className="results-screen min-h-screen bg-white">
        <TopBar
          step="Your diagnostic"
          right={
            <button
              onClick={() => window.print()}
              className="no-print inline-flex items-center gap-2 rounded-sm border border-neutral-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-700 hover:border-neutral-900"
              data-testid="results-download-pdf-button"
            >
              <Printer className="h-4 w-4" />
              Print / PDF
            </button>
          }
        />

      <main
        className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20"
        data-testid="results-dashboard-container"
      >
        <div className="tw-fade-up mb-14 border-b border-neutral-200 pb-10">
          <p className="tw-eyebrow">
            {data.pathway === "marketing" ? "Marketing pathway" : "Leadership pathway"}
          </p>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            {about.name ? `${about.name}, here's your diagnostic.` : "Your diagnostic."}
          </h1>
          {about.organisation && (
            <p className="mt-3 text-neutral-500">
              {about.job_title ? `${about.job_title} · ` : ""}
              {about.organisation}
            </p>
          )}
        </div>

        <ArchetypeSection
          primary={r.primary_archetype || r.dual_archetypes?.[0]}
          secondary={r.secondary_archetype || r.dual_archetypes?.[1]}
          showSecondary={r.secondary_display || r.archetype_state === "dual"}
          confidenceGap={r.archetype_confidence_gap}
        />
        <FrameworkSection ssp={r.ssp_normalised} ranked={r.ssp_ranked} />
        <YourThreeSection items={r.your_three} state={r.your_three_state} />
        <DimensionsSection
          status={r.dimension_status}
          ranked={r.dimension_ranked}
          descriptors={r.dimension_descriptors}
        />
        <InterpretationSection interpretation={r.interpretation} />
        <TalkItThroughSection />

        <div className="no-print mt-16 grid gap-8">
          <GoDeeper respondentId={data.respondent_id || id} available={data.go_deeper_available} />
          {!savedMode && <LeadCapture respondentId={id} about={about} />}
          <CounterpartInvite respondentId={data.respondent_id || id} pathway={data.pathway} />
        </div>
      </main>
      </div>

      {/* Dedicated print/PDF report — hidden on screen, rendered for print only */}
      <ResultsPrint data={data} />
    </>
  );
}
